import { zodResolver } from "@hookform/resolvers/zod";
import type { MeetingCreatePayload, Meeting } from "@stss/core/src/video-api";
import React from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";

import { Input, Button, Select, PhoneInput, Label, Tooltip } from "@calcom/ui";
import { Info } from "@calcom/ui/components/icon";

const MINUTES_OFFSET = 3;
const DAYS_OFFSET = 30;

const getMinTimeOffset = (minutes = MINUTES_OFFSET) => minutes * 60 * 1000;
const getMaxTimeOffset = (days = DAYS_OFFSET) => days * 24 * 60 * 60 * 1000;

const isMinTimeValid = (startTime: string): boolean => {
  const selectedTime = new Date(startTime);
  const currentTime = new Date(Date.now() + getMinTimeOffset());
  return selectedTime.getTime() >= currentTime.getTime();
};

const isMaxTimeValid = (startTime: string): boolean => {
  const selectedTime = new Date(startTime);
  const maxTime = new Date(Date.now() + getMaxTimeOffset());
  return selectedTime.getTime() <= maxTime.getTime();
};

const formatDateTime = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const getStartTime = (offsetMinutes = MINUTES_OFFSET) => {
  const now = new Date(Date.now() + getMinTimeOffset(offsetMinutes));
  return formatDateTime(now);
};

const getEndTime = () => {
  const now = new Date(Date.now() + getMaxTimeOffset());
  return formatDateTime(now);
};

export type FormData = MeetingCreatePayload;

export type MeetingFormProps = {
  languageOptions: { label: string; value: string }[];
  meeting?: Meeting;
  disabled?: boolean;
  providerId: number;
  onSubmit: (data: FormData) => void;
  onCancel: () => void;
};

export const MeetingForm = ({
  languageOptions = [{ label: "English", value: "en-US" }],
  meeting,
  providerId,
  disabled,
  onSubmit,
  onCancel,
}: MeetingFormProps) => {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: "all",
    resolver: zodResolver(
      z
        .object({
          providerId: z.number(),
          type: z.enum(["SCHEDULED", "ONE_TIME"]),
          startTime: z.string().min(1, { message: "Date is required" }),
          participant: z.object({
            email: z.string().email({ message: "Invalid email address" }),
            name: z.string().min(1, { message: "Name is required" }),
            phone: z.string().optional(),
            locale: z.string(),
          }),
        })
        .refine((data) => isMinTimeValid(data.startTime), {
          message: `This time should be at least ${MINUTES_OFFSET} minutes from the current time`,
          path: ["startTime"],
        })
        .refine((data) => isMaxTimeValid(data.startTime), {
          message: `This time should be at most ${DAYS_OFFSET} days from the current time`,
          path: ["startTime"],
        })
    ),
    defaultValues: {
      providerId: providerId,
      type: "ONE_TIME",
      startTime: meeting?.startTime || getStartTime(4),
      participant: {
        email: meeting?.participant.email || "",
        name: meeting?.participant.name || "",
        phone: meeting?.participant.phone || "",
        locale: meeting?.participant.locale || "en-US",
      },
    },
  });

  const submit: SubmitHandler<FormData> = async (data) => {
    const isValid = await trigger();
    if (isValid) {
      onSubmit(data);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(submit)} noValidate>
        <Controller
          name="providerId"
          control={control}
          render={({ field }) => <Input {...field} required type="hidden" readOnly />}
        />
        <Controller
          name="type"
          control={control}
          render={({ field }) => <Input {...field} required type="hidden" readOnly />}
        />
        <div className="flex flex-col">
          <fieldset disabled={disabled} className="w-full">
            <div className="flex w-full flex-col gap-0 md:flex-row md:gap-4">
              <div className="mb-10 w-full">
                <Label>
                  Start time*{" "}
                  <Tooltip
                    content={
                      <p className="max-w-96">
                        The translation service will be scheduled to start at this time. <br />
                        Please note that this time must be set to at least
                        <strong className="mx-1 text-orange-500">{MINUTES_OFFSET} minutes</strong> from the
                        current time. This requirement is due to technical reasons, as we need this buffer
                        period to ensure that the translation service is fully prepared and ready to deliver
                        the best possible experience.
                      </p>
                    }>
                    <span className="inline-block align-middle">
                      <Info className="h-4 w-4" />
                    </span>
                  </Tooltip>
                </Label>
                <Controller
                  name="startTime"
                  control={control}
                  render={({ field }) => {
                    return (
                      <input
                        type="datetime-local"
                        className="bg-default !disabled:bg-subtle w-full rounded-md disabled:cursor-not-allowed"
                        disabled={disabled}
                        min={getStartTime()}
                        max={getEndTime()}
                        value={field.value}
                        onChange={(evt) => {
                          field.onChange(evt.target.value);
                        }}
                      />
                    );
                  }}
                />
                {errors.startTime && <p className="mt-1 text-sm text-red-500">{errors.startTime.message}</p>}
              </div>
            </div>
            <h2 className="text-subtle mb-2">Participant details</h2>
            <div className="mb-6 flex w-full flex-col gap-3">
              <div>
                <Label>Name*</Label>
                <Controller
                  name="participant.name"
                  control={control}
                  render={({ field }) => <Input {...field} value={field.value || ""} type="text" required />}
                />
                {errors.participant?.name && (
                  <p className="text-sm text-red-500">{errors.participant?.name.message}</p>
                )}
              </div>
              <div>
                <Label>Email*</Label>
                <Controller
                  name="participant.email"
                  control={control}
                  render={({ field }) => <Input {...field} value={field.value || ""} type="email" required />}
                />
                {errors.participant?.email && (
                  <p className="text-sm text-red-500">{errors.participant?.email.message}</p>
                )}
              </div>
              <div>
                <Label>Phone</Label>
                <Controller
                  name="participant.phone"
                  control={control}
                  render={({ field: { value } }) => (
                    <PhoneInput
                      value={value || ""}
                      required
                      countryCodeEditable={false}
                      defaultCountry="us"
                      onChange={(value) => {
                        setValue("participant.phone", value);
                      }}
                    />
                  )}
                />
                {errors.participant?.phone && (
                  <p className="text-sm text-red-500">{errors.participant?.phone.message}</p>
                )}
              </div>
              <div>
                <Label>Locale*</Label>
                <Controller
                  name="participant.locale"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      isDisabled={disabled}
                      className="w-full"
                      placeholder="Locale"
                      options={languageOptions}
                      defaultValue={
                        languageOptions.find((opt) => opt.value === field.value) || languageOptions[0]
                      }
                      value={languageOptions.find((opt) => opt.value === field.value) || languageOptions[0]}
                      onChange={(opt) => {
                        if (!opt) return;
                        setValue("participant.locale", opt.value);
                      }}
                    />
                  )}
                />
                {errors.participant?.locale && (
                  <p className="text-sm text-red-500">{errors.participant?.locale.message}</p>
                )}
              </div>
            </div>
          </fieldset>
          <div className="flex justify-end gap-3">
            <Button type="button" onClick={onCancel} color="secondary">
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || disabled}>
              Submit
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

export default MeetingForm;
